import React, { useRef, useState } from "react";
import { View, StyleSheet, Platform, Linking, Pressable } from "react-native";
import { Text } from "react-native-paper";
import { COLORS, STRINGS } from "../../constants";
import Alert from "../Alert/Alert";
import { faTriangleExclamation, faLink } from "@fortawesome/free-solid-svg-icons";
import Heading from "../Heading/Heading";
import { WebView } from "react-native-webview";
/**
 * DetailDescription Component
 *
 * This component is designed to render detailed descriptions for various items or events,
 * incorporating optional warning alerts. It utilizes a title and a description to provide
 * contextual information and can conditionally display a disclaimer alert based on the
 * passed props.
 *
 * The disclaimer alert utilizes a FontAwesome icon to draw attention and provide visual
 * indication about the importance or nature of the disclaimer.
 *
 * Props:
 * - title (string): The title of the description section.
 * - description (string): The detailed text explaining more about the subject.
 * - disclaimer (boolean): A flag to determine if a disclaimer should be displayed.
 * - FontAwesomeIcon (component): The icon component used for rendering icons.
 * - iconSize (number): The size of the icon to be rendered within the alert.
 *
 * Usage:
 * The component is used within views that require detailed explanations with optional
 * alerts to highlight specific information or warnings. It is particularly useful in
 * settings where conditional alerts are necessary.
 *
 **/

export const shouldStartLoadWithRequest = (request) => {
  // Check if the URL should be opened externally (e.g., if it's not part of your app's domain)
  if (request.url.startsWith("http") || request.url.startsWith("https")) {
    Linking.openURL(request.url).catch((err) => console.error("Failed to open URL:", err));
    return false; // Prevents the WebView from loading the URL
  }
  return true; // Allows the WebView to load the URL
};

const DetailDescription = ({ title, description, disclaimer = false, FontAwesomeIcon, iconSize, eventUrl }) => {
  const webViewRef = useRef(null);
  const [webViewHeight, setWebViewHeight] = useState(100);
  const fontSize = 16;

  const injectedJS = `
    function sendHeight() {
      const height = document.documentElement.scrollHeight || document.body.scrollHeight;
      window.ReactNativeWebView.postMessage(height.toString());
    }
  
    if (document.readyState === 'complete') {
      sendHeight();
    } else {
      window.addEventListener('load', sendHeight, { once: true });
    }
    true;
  `;

  const shouldStartLoadWithRequest = (request) => {
    // Check if the URL should be opened externally (e.g., if it's not part of your app's domain)
    if (request.url.startsWith("http") || request.url.startsWith("https")) {
      Linking.openURL(request.url).catch((err) => console.error("Failed to open URL:", err));
      return false; // Prevents the WebView from loading the URL
    }
    return true; // Allows the WebView to load the URL
  };

  const containsHTML = (text) => {
    const htmlRegex = /<\/?[a-z][\s\S]*>/i;
    return htmlRegex.test(text);
  };

  return (
    <View style={styles.container}>
      <Heading fontSize={18} text={title} color={COLORS.textDark} style={styles.title} />
      {eventUrl && (
        <Pressable
          style={styles.linkButton}
          onPress={() => Linking.openURL(eventUrl)}
          accessibilityLabel="Open event link"
          accessibilityRole="button"
          accessibilityHint="Opens the event website in your browser"
        >
          <FontAwesomeIcon icon={faLink} size={iconSize} color={COLORS.textLight} />
          <Text style={styles.linkButtonText}>{STRINGS.website}</Text>
        </Pressable>
      )}
      {disclaimer && (
        <Alert
          FontAwesomeIcon={FontAwesomeIcon}
          iconProps={{
            icon: faTriangleExclamation,
            color: COLORS.warningText,
            size: iconSize,
          }}
          backgroundColor={COLORS.warningLight}
          title={STRINGS.disclaimer}
          description={STRINGS.disclaimerText}
          titleColor={COLORS.warningText}
          descriptionColor={COLORS.warningText}
        />
      )}

      {Platform.OS !== "web" && containsHTML(description) && (
        <View style={{ height: webViewHeight }}>
          <WebView
            ref={webViewRef}
            style={{
              height: webViewHeight,
            }}
            limitsNavigationsToAppBoundDomains={true}
            source={{ html: description }}
            originWhitelist={["*"]}
            javaScriptEnabled={true}
            scrollEnabled={false}
            injectedJavaScript={injectedJS}
            onShouldStartLoadWithRequest={shouldStartLoadWithRequest}
            onMessage={(event) => {
              const height = Number(event.nativeEvent.data);
              if (!isNaN(height)) {
                setWebViewHeight(height);
              }
            }}
          />
        </View>
      )}

      {Platform.OS === "web" && containsHTML(description) && (
        <div
          style={{
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          }}
          dangerouslySetInnerHTML={{ __html: description }}
        />
      )}

      {!containsHTML(description) && <Text style={[styles.description, { fontSize: fontSize }]}>{description}</Text>}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 32,
  },
  title: {
    marginBottom: 12,
  },
  description: {
    lineHeight: 26,
    color: COLORS.border,
  },
  linkButton: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    backgroundColor: COLORS.primary,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 30,
    marginTop: 4,
    marginBottom: 4,
    gap: 8,
  },
  linkButtonText: {
    fontSize: 16,
    fontWeight: Platform.OS === 'web' ? '500' : 'bold',
    color: COLORS.textLight,
  },
});

export default DetailDescription;
